import { useCallback, useEffect, useRef, useState } from "react";
import { loadPdfJs, loadPdfDocumentFromBytes } from "./pdfjsLoader";
import {
  computeFitScale,
  isRasterImageFilename,
  isRasterLikeSource,
  pageViewportLooksLikeRaster,
} from "./pdfFitUtils";

const previewFrameStyle = { position: "absolute", left: 0, top: 0, width: "100%", height: "100%", border: "none" };
const previewMsgStyle = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "rgba(255,255,255,0.5)",
  fontSize: 13,
  margin: 0,
  padding: "0 16px",
  textAlign: "center",
};

function getUserAgent() {
  return typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
}

/** iOS / iPadOS — native PDF in iframe, DOM often empty. */
function isIosSafariLike() {
  const ua = getUserAgent();
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return typeof navigator !== "undefined" && navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

/**
 * Browsers that render PDFs via a native plugin in blob iframes (DOM often empty).
 * Desktop Chrome/Edge must be included — otherwise we false-positive into PDF.js and show an error.
 * Android Chrome / Samsung / WebView: same as iOS. Brave (any platform) needs iframe probe + PDF.js fallback.
 */
function usesNativePdfPluginWithoutDom() {
  if (isIosSafariLike()) return true;
  const ua = getUserAgent();
  if (!/Android/i.test(ua)) {
    if (/Brave/i.test(ua)) return false;
    if (/Edg\//i.test(ua)) return true;
    if (/Chrome|Chromium/i.test(ua)) return true;
    return false;
  }
  if (/Brave/i.test(ua)) return false;
  if (/Firefox|FxiOS/i.test(ua)) return false;
  return /Chrome|Chromium|CriOS|SamsungBrowser|EdgA/i.test(ua);
}

function shouldProbeIframeForBrokenPdf() {
  return !usesNativePdfPluginWithoutDom();
}

function isIframePdfLikelyBroken(iframe) {
  if (!iframe || !shouldProbeIframeForBrokenPdf()) return false;
  try {
    const doc = iframe.contentDocument;
    if (!doc?.body) return false;
    const pdfEl = doc.querySelector('embed[type*="pdf"], object[type*="pdf"], embed, object');
    if (pdfEl && (pdfEl.offsetWidth > 40 || pdfEl.offsetHeight > 40)) return false;
    const body = doc.body;
    const html = (body.innerHTML || "").replace(/\s/g, "");
    if (body.childElementCount === 0 && html.length === 0) return true;
    return false;
  } catch {
    return false;
  }
}

function previewModeForFile(originalFilename) {
  return isRasterImageFilename(originalFilename) ? "pdfjs" : "iframe";
}

/**
 * Try native iframe PDF first; fall back to PDF.js only if preview looks broken.
 * Raster uploads (PNG/JPG → PDF) use PDF.js with fit-to-container scaling.
 */
export function PdfResultPreview({ blobUrl, title = "PDF preview", originalFilename = "" }) {
  const iframeRef = useRef(null);
  const [mode, setMode] = useState(() => previewModeForFile(originalFilename));
  const checkTimerRef = useRef(null);

  const scheduleBrokenCheck = useCallback(() => {
    if (!shouldProbeIframeForBrokenPdf()) return;
    if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    let attempts = 0;
    const probe = () => {
      const iframe = iframeRef.current;
      if (!iframe) return;
      if (!isIframePdfLikelyBroken(iframe)) return;
      attempts += 1;
      if (attempts >= 3) {
        setMode("pdfjs");
        return;
      }
      checkTimerRef.current = setTimeout(probe, 800);
    };
    checkTimerRef.current = setTimeout(probe, 900);
  }, []);

  useEffect(() => {
    setMode(previewModeForFile(originalFilename));
    return () => {
      if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    };
  }, [blobUrl, originalFilename]);

  if (mode === "pdfjs") {
    return <PdfBlobPreview blobUrl={blobUrl} title={title} originalFilename={originalFilename} />;
  }

  return (
    <iframe
      ref={iframeRef}
      src={`${blobUrl}#toolbar=0&navpanes=0&view=FitH`}
      style={previewFrameStyle}
      title={title}
      onLoad={scheduleBrokenCheck}
      onError={() => setMode("pdfjs")}
    />
  );
}

/**
 * PDF.js canvas stack — used for raster PDFs and when iframe preview fails.
 */
export default function PdfBlobPreview({ blobUrl, title = "PDF preview", originalFilename = "" }) {
  const scrollRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!blobUrl) {
      setStatus("error");
      return;
    }

    let cancelled = false;
    let pdfDoc = null;

    (async () => {
      try {
        const pdfjs = await loadPdfJs();
        pdfDoc = await loadPdfDocumentFromBytes(pdfjs, await (await fetch(blobUrl)).arrayBuffer());
        if (cancelled) return;

        const container = scrollRef.current;
        if (!container) return;
        container.replaceChildren();

        const containerWidth = Math.max(container.clientWidth || 320, 280);
        const containerHeight = Math.max(container.clientHeight || 480, 280);
        const firstPage = await pdfDoc.getPage(1);
        const firstBase = firstPage.getViewport({ scale: 1 });
        const rasterLike = isRasterLikeSource(originalFilename, firstBase);

        for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum += 1) {
          if (cancelled) return;
          const page = await pdfDoc.getPage(pageNum);
          const baseViewport = page.getViewport({ scale: 1 });
          let scale;
          if (rasterLike || pageViewportLooksLikeRaster(baseViewport)) {
            const pageGap = pdfDoc.numPages > 1 ? 4 : 0;
            const maxPageHeight = pdfDoc.numPages > 1
              ? Math.max((containerHeight - pageGap * (pdfDoc.numPages - 1)) / pdfDoc.numPages, 120)
              : containerHeight;
            scale = computeFitScale(baseViewport, {
              clientWidth: containerWidth,
              clientHeight: maxPageHeight,
            }, 0);
          } else {
            scale = containerWidth / baseViewport.width;
          }
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("canvas 2d unavailable");
          const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
          canvas.width = Math.floor(viewport.width * dpr);
          canvas.height = Math.floor(viewport.height * dpr);
          canvas.style.display = "block";
          canvas.style.width = rasterLike ? `${viewport.width}px` : "100%";
          canvas.style.height = "auto";
          canvas.style.margin = "0 auto";
          canvas.style.background = "#fff";
          if (pageNum < pdfDoc.numPages) canvas.style.marginBottom = "4px";

          ctx.scale(dpr, dpr);
          await page.render({ canvasContext: ctx, viewport }).promise;
          if (!cancelled) container.appendChild(canvas);
        }

        if (!cancelled) setStatus("ready");
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
      pdfDoc?.destroy?.();
    };
  }, [blobUrl, originalFilename]);

  const boxStyle = {
    position: "absolute",
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    overflowY: "auto",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
    background: "#000",
  };

  return (
    <>
      <div ref={scrollRef} style={boxStyle} aria-label={title} />
      {status === "loading" && <p style={previewMsgStyle}>Caricamento anteprima…</p>}
      {status === "error" && <p style={previewMsgStyle}>Anteprima PDF non disponibile in questo browser.</p>}
    </>
  );
}
